/**
 * ITEM_TYPES Configuration
 * 
 * This file provides utilities for working with Item Types.
 * All item type values must be dynamically fetched from the item_masters table
 * via the API, and never hardcoded.
 * 
 * All item-type related values must be dynamically referenced from this file.
 */

import React from 'react';
import { masterDropdownService, MasterOption } from '../services/masterDropdownService';

/**
 * Item Type Option interface
 */
export interface ItemTypeOption {
  value: string;
  label: string;
}

/**
 * Cache for item types to avoid repeated API calls
 */
let itemTypesCache: ItemTypeOption[] | null = null;
let itemTypesCachePromise: Promise<ItemTypeOption[]> | null = null;

/**
 * Get all item types from the API (cached)
 * @returns Promise resolving to array of item type options
 */
export async function getItemTypes(): Promise<ItemTypeOption[]> {
  // Return cached value if available
  if (itemTypesCache) {
    return itemTypesCache;
  }

  // Return existing promise if already fetching
  if (itemTypesCachePromise) {
    return itemTypesCachePromise;
  }

  // Fetch from API
  itemTypesCachePromise = masterDropdownService
    .getItemsDropdown()
    .then((options: MasterOption[]) => {
      const itemTypes: ItemTypeOption[] = options.map(opt => ({
        value: String(opt.value), // Ensure value is always a string
        label: String(opt.label),
      }));
      itemTypesCache = itemTypes;
      itemTypesCachePromise = null;
      return itemTypes;
    })
    .catch((error) => {
      console.error('Error fetching item types:', error);
      itemTypesCachePromise = null;
      return [];
    });

  return itemTypesCachePromise;
}

/**
 * Clear the item types cache (useful after updates)
 */
export function clearItemTypesCache(): void {
  itemTypesCache = null;
  itemTypesCachePromise = null;
}

/**
 * Get item type values as an array of strings
 * @returns Promise resolving to array of item type values
 */
export async function getItemTypeValues(): Promise<string[]> {
  const itemTypes = await getItemTypes();
  return itemTypes.map(it => it.value);
}

/**
 * Get item type labels as an array of strings
 * @returns Promise resolving to array of item type labels
 */
export async function getItemTypeLabels(): Promise<string[]> {
  const itemTypes = await getItemTypes();
  return itemTypes.map(it => it.label);
}

/**
 * Check if a value is a valid item type
 * @param value The value to check
 * @returns Promise resolving to boolean indicating if value is valid
 */
export async function isValidItemType(value: string): Promise<boolean> {
  const itemTypes = await getItemTypes();
  return itemTypes.some(it => it.value === value || it.label === value);
}

/**
 * Get item type label by value
 * @param value The item type value
 * @returns Promise resolving to the label or undefined if not found
 */
export async function getItemTypeLabel(value: string): Promise<string | undefined> {
  const itemTypes = await getItemTypes();
  const itemType = itemTypes.find(it => it.value === value || it.label === value);
  return itemType?.label;
}

/**
 * Get item type value by label
 * @param label The item type label
 * @returns Promise resolving to the value or undefined if not found
 */
export async function getItemTypeValue(label: string): Promise<string | undefined> {
  const itemTypes = await getItemTypes();
  const itemType = itemTypes.find(it => it.label === label || it.value === label);
  return itemType?.value;
}

/**
 * React hook to get item types (for use in components)
 * This is a convenience wrapper that can be used in React components
 */
export function useItemTypes() {
  const [itemTypes, setItemTypes] = React.useState<ItemTypeOption[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    getItemTypes()
      .then((types) => {
        setItemTypes(types);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || 'Failed to load item types');
        setLoading(false);
      });
  }, []);

  const refetch = React.useCallback(() => {
    clearItemTypesCache();
    return getItemTypes();
  }, []);

  return { itemTypes, loading, error, refetch };
}

/**
 * Helper functions for item type comparisons
 * These functions check if a value matches an item type by comparing against
 * the dynamically loaded item types from the database.
 */

/**
 * Check if a value matches an item type (case-insensitive)
 * @param value The value to check
 * @param itemTypeName The item type name to match against (e.g., "Frame", "Panel")
 * @returns Promise resolving to boolean
 */
export async function isItemType(value: string, itemTypeName: string): Promise<boolean> {
  const itemTypes = await getItemTypes();
  const normalizedValue = value.toLowerCase().trim();
  const normalizedItemType = itemTypeName.toLowerCase().trim();
  
  return itemTypes.some(it => {
    const itValue = it.value.toLowerCase().trim();
    const itLabel = it.label.toLowerCase().trim();
    return (itValue === normalizedItemType || itLabel === normalizedItemType) &&
           (itValue === normalizedValue || itLabel === normalizedValue);
  });
}

/**
 * Get an item type value by a common name (case-insensitive)
 * Useful for getting the actual database value from a common name
 * @param commonName Common name like "Frame", "Panel", "Panel Window", "Net Fixing"
 * @returns Promise resolving to the item type value or undefined
 */
export async function getItemTypeByCommonName(commonName: string): Promise<string | undefined> {
  const itemTypes = await getItemTypes();
  const normalized = commonName.toLowerCase().trim();
  
  // Common name mappings (these should ideally come from the database)
  const commonNameMap: Record<string, string[]> = {
    'frame': ['frame'],
    'panel': ['panel'],
    'panel window': ['panel window', 'panelwindow'],
    'net fixing': ['net fixing', 'netfix', 'net'],
    'openable type': ['openable type', 'openable'],
  };
  
  const possibleNames = commonNameMap[normalized] || [normalized];
  
  for (const it of itemTypes) {
    const itValue = it.value.toLowerCase().trim();
    const itLabel = it.label.toLowerCase().trim();
    if (possibleNames.some(name => itValue === name || itLabel === name)) {
      return it.value;
    }
  }
  
  return undefined;
}

